import React from 'react';
import { Paper, Typography, styled } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Edit from '@mui/icons-material/Edit';
import { IconButton } from 'ui/buttons/IconButton';
import { Column, Row } from 'ui/Flex';
import { CampaignItem, conditionLabels } from './models/CampaignItem';
import { campaignTriggerConditionLabel } from './models/CampaignTriggerCondition';
import { campaignTriggerEffectLabel } from './models/CampaignTriggerEffect';

const PREFIX = 'CampaignSettingsView';

const classes = {
    icon: `${PREFIX}-icon`,
    column: `${PREFIX}-column`,
    button: `${PREFIX}-button`,
    bulleted: `${PREFIX}-bulleted`
};

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4, 4, 5, 3),
    position: 'relative',
    [`& .${classes.icon}`]: {
        paddingRight: theme.spacing(2),
        paddingTop: theme.spacing(1)
    },
    [`& .${classes.column}`]: {
        paddingRight: theme.spacing(3)
    },
    [`& .${classes.button}`]: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(1)
    },
    [`& .${classes.bulleted}`]: {
        display: 'list-item',
        listStyleType: 'disc',
        listStylePosition: 'inside'
    }
}));

type CampaignSettingsViewProps = {
    className?: string;
    settingsData: CampaignItem[];
    onEdit: () => void;
};

export const CampaignSettingsView: React.FC<CampaignSettingsViewProps> = props => {
    const { settingsData, className, onEdit } = props;
    const validSettingsData = settingsData.filter(
        (campaignItem: CampaignItem) =>
            campaignItem.id !== '' && campaignItem.condition && campaignItem.effect
    );

    const generateSummaryForItem = (item: CampaignItem) => {
        const conditionLabel = campaignTriggerConditionLabel[item.condition.type].toLowerCase();
        const effectLabel = campaignTriggerEffectLabel[item.effect.type].toLowerCase();
        const conditiontimeConditionLabel = item.condition.value
            .map(value => conditionLabels[value.key].toLowerCase())
            .join(' and ');
        const effectPointsLabel = item.effect.points ? `with ${item.effect.points} stamps/points` : '';
        return `When ${conditionLabel} ${conditiontimeConditionLabel}, Then ${effectLabel} "${item.effect.target}" ${effectPointsLabel}`;
    };
    return (
        <StyledPaper className={className}>
            <Row flex={1} align="space-between" valign="flex-start">
                <div className={classes.icon}>
                    <CheckCircleIcon color="primary" />
                </div>
                <Column flex={1} className={classes.column}>
                    <Typography variant="h5">Configure</Typography>
                    {validSettingsData.length === 0 && (
                        <Typography variant="body2">You have no defined Conditions and Effects</Typography>
                    )}
                    {validSettingsData.length !== 0 && (
                        <Typography variant="body2">
                            You defined the following Conditions and Effects:
                        </Typography>
                    )}
                    {validSettingsData.length !== 0 &&
                        validSettingsData.map((campaignItem: CampaignItem) => (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                key={campaignItem.id}
                                className={classes.bulleted}
                            >
                                {generateSummaryForItem(campaignItem)}
                            </Typography>
                        ))}
                </Column>
            </Row>
            <IconButton className={classes.button} onClick={onEdit} size="large">
                <Edit />
            </IconButton>
        </StyledPaper>
    );
};
